import Box from '@mui/material/Box';
import { DataGrid } from '@mui/x-data-grid';
import type { DataGridProps, GridValidRowModel } from '@mui/x-data-grid';

export interface DataTableProps<R extends GridValidRowModel> extends DataGridProps<R> {
  /** Fixed pixel height. Omit to let the grid size to its rows. */
  height?: number | string;
}

/**
 * The platform's paginated grid.
 *
 * Used by the platform's own screens — Users, Audit Log, Database Usage, Talogent
 * Users — where a pager is the intended behaviour.
 *
 * NOT for screens ported from a source app that shows every row at once. This is MUI X
 * DataGrid **Community**, which caps a page at 100 rows and rejects `pagination={false}`
 * (a Pro feature) with:
 *
 *   "Invalid prop `pagination` of value `false` supplied to DataGrid,
 *    expected one of [true]."
 *
 * Passing `hideFooter` to fake an unpaginated table therefore does NOT work — the grid
 * keeps paginating with the pager hidden, and the rows past the first page become
 * unreachable with nothing on screen to say so. That is exactly how 476 of Application
 * Master Data's 576 rows disappeared. Those screens use `ScrollTable` instead, which
 * reproduces APR's own all-rows-on-one-surface table.
 *
 * Community is deliberate: it keeps the baseline licence cost at zero. Needing a Pro
 * feature is a separate, explicit decision and would change only this file.
 */
export function DataTable<R extends GridValidRowModel>({
  height,
  autoHeight,
  pageSizeOptions = [10, 25, 50, 100],
  initialState,
  ...props
}: DataTableProps<R>) {
  return (
    <Box sx={{ width: '100%', ...(height ? { height } : {}) }}>
      <DataGrid<R>
        autoHeight={height ? false : (autoHeight ?? true)}
        pageSizeOptions={pageSizeOptions}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
          ...initialState,
        }}
        {...props}
      />
    </Box>
  );
}
